import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { AgentSummary, PropertyRecord, PropertyAdminView, UserSummary } from '../api/types'
import { Banner } from '../components/Banner'
import { ConfigList, ConfigRow, type RowChip } from '../components/ConfigList'
import { Drawer } from '../components/Drawer'
import { Credentials } from '../components/Credentials'
import { PropertyPicker } from '../components/PropertyPicker'

interface Props {
  /** The signed-in administrator, so the UI never offers to delete them. */
  currentUser: string
}

const MINIMUM_PASSWORD = 8
const PRESETS = ['editorial', 'editorial_commerce', 'ecommerce'] as const

/**
 * The Admin page's sections.
 *
 * Tabs rather than one long scroll: the page mixes four unrelated jobs, and the two
 * destructive ones (deleting a person, removing a credential) should not sit a
 * mis-scroll away from the one people open most.
 */
const ADMIN_TABS = [
  { id: 'properties', label: 'Properties' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'people', label: 'People' },
  { id: 'agents', label: 'Agents' },
] as const

type AdminTab = (typeof ADMIN_TABS)[number]['id']

function blankProperty(): PropertyRecord {
  return {
    property_id: '',
    display_name: '',
    preset: 'editorial',
    has_paywall: false,
    site_label: 'site',
    credential_file: '',
    alert_chat_id: '',
  }
}

function describe(properties: string[]): string {
  if (properties.includes('*')) return 'All properties'
  if (properties.length === 0) return 'Nothing assigned'
  return `${properties.length} propert${properties.length === 1 ? 'y' : 'ies'}`
}

/**
 * Accounts, agent tokens and the property list.
 *
 * Three concerns on one screen because they are one job: a property is only useful once
 * somebody is assigned to it, and an assignment is only meaningful once the property
 * exists. Splitting them into tabs would mean pinging between two of them to add one site.
 */
export function Admin({ currentUser }: Props) {
  const [users, setUsers] = useState<UserSummary[] | null>(null)
  const [agents, setAgents] = useState<AgentSummary[] | null>(null)
  const [config, setConfig] = useState<PropertyAdminView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Shown once, in the UI, right after creation — the server never returns it again.
  const [issuedToken, setIssuedToken] = useState<{ name: string; token: string } | null>(null)
  const [tab, setTab] = useState<AdminTab>('properties')

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      api.adminUsers(controller.signal),
      api.adminAgents(controller.signal),
      api.adminProperties(controller.signal),
    ])
      .then(([loadedUsers, loadedAgents, loadedConfig]) => {
        setUsers(loadedUsers)
        setAgents(loadedAgents)
        setConfig(loadedConfig)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'Could not load the admin data.')
      })
    return () => controller.abort()
  }, [])

  async function run<T>(action: () => Promise<T>, message: string): Promise<T | null> {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const result = await action()
      setNotice(message)
      return result
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'That did not work.')
      return null
    } finally {
      setBusy(false)
    }
  }

  const propertyIds = (config?.properties ?? []).map((entry) => entry.property_id)

  if (error && !users) return <Banner tone="critical">{error}</Banner>
  if (!users || !agents || !config) return null

  return (
    <div className="admin">
      {error && <Banner tone="critical">{error}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}

      <nav className="admin-tabs" aria-label="Admin sections">
        {ADMIN_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-tabs__tab${tab === item.id ? ' admin-tabs__tab--active' : ''}`}
            aria-current={tab === item.id ? 'page' : undefined}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === 'properties' && (
        <Properties
          config={config}
          busy={busy}
          onSave={async (records) => {
            const saved = await run(() => api.saveAdminProperties(records), 'Properties saved.')
            if (saved) setConfig(saved)
          }}
        />
      )}

      {tab === 'credentials' && (
        <Credentials
          config={config}
          busy={busy}
          run={run}
          onChanged={async () => {
            const refreshed = await api.adminProperties()
            setConfig(refreshed)
          }}
        />
      )}

      {tab === 'people' && (
        <Users
          users={users}
          currentUser={currentUser}
          propertyIds={propertyIds}
          busy={busy}
          onChange={setUsers}
          run={run}
        />
      )}

      {tab === 'agents' && (
        <Agents
          agents={agents}
          propertyIds={propertyIds}
          busy={busy}
          issuedToken={issuedToken}
          onIssued={setIssuedToken}
          onChange={setAgents}
          run={run}
        />
      )}
    </div>
  )
}

// --- properties ---

function Properties({
  config,
  busy,
  onSave,
}: {
  config: PropertyAdminView
  busy: boolean
  onSave: (records: PropertyRecord[]) => void
}) {
  const [draft, setDraft] = useState<PropertyRecord[]>(config.properties)
  const [editing, setEditing] = useState<number | null>(null)
  const [testing, setTesting] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; message: string } | null>(null)

  function update(index: number, patch: Partial<PropertyRecord>) {
    setDraft((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)))
  }

  /**
   * The saved record behind a row, or undefined for one that has never been saved.
   *
   * The test sends to what is *stored*, so testing a row whose chat id has been edited
   * but not saved would report success for the previous destination — the one failure a
   * test button must never produce.
   */
  function savedRow(property: PropertyRecord): PropertyRecord | undefined {
    return config.properties.find((item) => item.property_id === property.property_id)
  }

  async function runTest(property: PropertyRecord) {
    setTesting(property.property_id)
    setTestResult(null)
    try {
      const result = await api.sendTestAlert(property.property_id)
      setTestResult({
        id: property.property_id,
        ok: true,
        message: `Sent to ${result.chat_id}. If it did not arrive, the bot is not in that chat.`,
      })
    } catch (error) {
      setTestResult({
        id: property.property_id,
        ok: false,
        message: error instanceof Error ? error.message : 'The test could not be sent.',
      })
    } finally {
      setTesting(null)
    }
  }

  const open = editing === null ? undefined : draft[editing]

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Properties</h2>
        <span className="section__note">Takes effect immediately — no restart</span>
      </div>

      {config.credentials.length === 0 && (
        <Banner tone="warning">
          <strong>No service-account key has been added yet.</strong> Nothing can fetch from
          GA4 until one exists — add it on the Credentials tab.
        </Banner>
      )}

      {config.shadowed_environment_property && (
        <Banner tone="warning">
          <strong>
            Property {config.shadowed_environment_property} is set in the environment but is
            not in this list, so it is no longer served.
          </strong>{' '}
          The property file replaces those variables rather than adding to them. If dropping
          it was deliberate, clear <code>GA4_PROPERTY_ID</code> from <code>.env</code>;
          otherwise add it back here.
        </Banner>
      )}

      {draft.length === 0 ? (
        <div className="card">
          <p className="admin__empty">No properties configured yet.</p>
        </div>
      ) : (
        <ConfigList>
          {draft.map((property, index) => {
            const chips: RowChip[] = [{ key: 'preset', text: property.preset }]
            if (property.has_paywall) chips.push({ key: 'paywall', text: 'Paywall' })
            // Unrouted is the state worth seeing from the list: alerts are still
            // collected, so nothing looks broken until one needed to arrive.
            chips.push(
              property.alert_chat_id
                ? { key: 'alerts', text: 'Alerts routed', tone: 'ok' }
                : { key: 'alerts', text: 'Alerts unrouted', tone: 'warning' },
            )
            return (
              <ConfigRow
                key={index}
                name={property.display_name || 'Unnamed property'}
                code={property.property_id}
                summary={`Seen by models as “${property.site_label || 'site'}” · ${
                  property.credential_file || 'default credential'
                }`}
                chips={chips}
                active={editing === index}
                onOpen={() => setEditing(index)}
              />
            )
          })}
        </ConfigList>
      )}

      <div className="controls" style={{ marginTop: 16 }}>
        <button type="button" className="button" disabled={busy} onClick={() => onSave(draft)}>
          Save properties
        </button>
        <button
          type="button"
          className="button secondary"
          disabled={busy}
          onClick={() => {
            setDraft((current) => [...current, blankProperty()])
            setEditing(draft.length)
          }}
        >
          Add property
        </button>
      </div>

      <Drawer
        open={open !== undefined}
        title={open?.display_name || 'New property'}
        subtitle={open?.property_id || 'No property id yet'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              type="button"
              className="button danger"
              disabled={busy}
              onClick={() => {
                setDraft((current) => current.filter((_, position) => position !== editing))
                setEditing(null)
              }}
            >
              Remove
            </button>
            <button type="button" className="button" disabled={busy} onClick={() => onSave(draft)}>
              Save properties
            </button>
          </>
        }
      >
        {open !== undefined && editing !== null && (
          <>
            <div className="drawer__grid">
              <label className="field">
                <span className="field__label">Property ID</span>
                <input
                  value={open.property_id}
                  inputMode="numeric"
                  placeholder="309949697"
                  onChange={(event) => update(editing, { property_id: event.target.value.trim() })}
                />
              </label>

              <label className="field">
                <span className="field__label">Display name</span>
                <input
                  value={open.display_name}
                  placeholder="Il Foglio"
                  onChange={(event) => update(editing, { display_name: event.target.value })}
                />
              </label>

              <label className="field">
                <span className="field__label">Preset</span>
                <select
                  value={open.preset}
                  onChange={(event) => update(editing, { preset: event.target.value })}
                >
                  {PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Credential</span>
                <select
                  value={open.credential_file}
                  onChange={(event) => update(editing, { credential_file: event.target.value })}
                >
                  <option value="">Default</option>
                  {config.credentials.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="field">
              <span className="field__label">Site label</span>
              <input
                value={open.site_label}
                placeholder="site"
                onChange={(event) => update(editing, { site_label: event.target.value })}
              />
              <span className="field__note">The only name an external model sees</span>
            </label>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={open.has_paywall}
                onChange={(event) => update(editing, { has_paywall: event.target.checked })}
              />
              Has a paywall &mdash; adds the Paywall tab for this property
            </label>

            <p className="drawer__section">Alert delivery</p>

            <label className="field">
              <span className="field__label">Alert chat</span>
              <input
                value={open.alert_chat_id}
                placeholder="-1001234567890"
                onChange={(event) => update(editing, { alert_chat_id: event.target.value })}
              />
              <span className="field__note">
                Telegram chat id for this property alone. Left empty, its alerts are collected
                and never delivered &mdash; there is no shared fallback, so one client&rsquo;s
                alerts cannot reach another&rsquo;s channel.
              </span>
            </label>

            <div className="controls" style={{ margin: 0 }}>
              <button
                type="button"
                className="button secondary"
                disabled={
                  busy ||
                  testing !== null ||
                  savedRow(open)?.alert_chat_id !== open.alert_chat_id ||
                  !open.alert_chat_id
                }
                onClick={() => void runTest(open)}
              >
                {testing === open.property_id ? 'Sending…' : 'Send test'}
              </button>
              <span className="field__note">
                {!open.alert_chat_id
                  ? 'Set an alert chat first.'
                  : savedRow(open)?.alert_chat_id !== open.alert_chat_id
                    ? 'Save first — the test sends to the stored chat id, not the edited one.'
                    : 'Sends a real alert, formatted exactly like a live one.'}
              </span>
            </div>

            {testResult?.id === open.property_id && (
              <span className={testResult.ok ? 'field__note' : 'field__note field__note--error'}>
                {testResult.message}
              </span>
            )}
          </>
        )}
      </Drawer>
    </section>
  )
}

// --- users ---

function Users({
  users,
  currentUser,
  propertyIds,
  busy,
  onChange,
  run,
}: {
  users: UserSummary[]
  currentUser: string
  propertyIds: string[]
  busy: boolean
  onChange: (users: UserSummary[]) => void
  run: <T>(action: () => Promise<T>, message: string) => Promise<T | null>
}) {
  const [adding, setAdding] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [admin, setAdmin] = useState(false)
  const [canConfigure, setCanConfigure] = useState(true)
  const [assigned, setAssigned] = useState<string[]>([])
  const [editing, setEditing] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')

  const ready = username.trim().length > 0 && password.length >= MINIMUM_PASSWORD

  async function add() {
    const saved = await run(
      () =>
        api.createUser({
          username: username.trim(),
          password,
          display_name: displayName.trim(),
          admin,
          properties: assigned,
          can_configure: canConfigure,
        }),
      `${username.trim()} can now sign in.`,
    )
    if (saved) {
      onChange(saved)
      setAdding(false)
      setUsername('')
      setPassword('')
      setDisplayName('')
      setAdmin(false)
      setCanConfigure(true)
      setAssigned([])
    }
  }

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">People</h2>
        <span className="section__note">Assignment changes apply on their next request</span>
      </div>

      <div className="card admin__card">
        {users.map((user) => (
          <div className="admin__person" key={user.username}>
            <div className="admin__identity">
              <strong>{user.display_name || user.username}</strong>
              <span className="admin__meta">
                {user.username}
                {user.admin && <span className="admin__badge">admin</span>}
                {user.username === currentUser && <span className="admin__badge">you</span>}
              </span>
            </div>

            <PropertyPicker
              available={propertyIds}
              selected={user.properties}
              disabled={busy}
              onChange={async (properties) => {
                const saved = await run(
                  () => api.updateUser(user.username, { properties }),
                  `${user.username} now sees ${describe(properties).toLowerCase()}.`,
                )
                if (saved) onChange(saved)
              }}
            />

            <label className="checkbox">
              <input
                type="checkbox"
                checked={user.can_configure}
                // An administrator always may, so the control would be a lie that
                // reverts on the next render.
                disabled={busy || user.admin}
                onChange={async (event) => {
                  const allowed = event.target.checked
                  const saved = await run(
                    () => api.updateUser(user.username, { can_configure: allowed }),
                    allowed
                      ? `${user.username} can change settings.`
                      : `${user.username} can no longer change settings.`,
                  )
                  if (saved) onChange(saved)
                }}
              />
              <span>
                Can change settings
                {user.admin && <span className="field__note"> — administrators always can</span>}
              </span>
            </label>

            <div className="admin__person-actions">
              {editing === user.username ? (
                <>
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="button"
                    disabled={busy || newPassword.length < MINIMUM_PASSWORD}
                    onClick={async () => {
                      const saved = await run(
                        () => api.updateUser(user.username, { password: newPassword }),
                        `Password changed for ${user.username}.`,
                      )
                      if (saved) {
                        onChange(saved)
                        setEditing(null)
                        setNewPassword('')
                      }
                    }}
                  >
                    Save
                  </button>
                  <button type="button" className="button secondary" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="button secondary"
                    disabled={busy}
                    onClick={() => {
                      setEditing(user.username)
                      setNewPassword('')
                    }}
                  >
                    Set password
                  </button>
                  {user.username !== currentUser && (
                    <>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={busy}
                        onClick={async () => {
                          const saved = await run(
                            () => api.updateUser(user.username, { admin: !user.admin }),
                            `${user.username} is ${user.admin ? 'no longer' : 'now'} an administrator.`,
                          )
                          if (saved) onChange(saved)
                        }}
                      >
                        {user.admin ? 'Revoke admin' : 'Make admin'}
                      </button>
                      <button
                        type="button"
                        className="button danger"
                        disabled={busy}
                        onClick={async () => {
                          const saved = await run(
                            () => api.deleteUser(user.username),
                            `Removed ${user.username}.`,
                          )
                          if (saved) onChange(saved)
                        }}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {adding ? (
          <div className="admin__row admin__row--new">
            <label className="field">
              <span className="field__label">Username</span>
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoFocus />
            </label>
            <label className="field">
              <span className="field__label">Display name</span>
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <span className="field__note">{MINIMUM_PASSWORD} characters or more</span>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={admin} onChange={(event) => setAdmin(event.target.checked)} />
              <span>Administrator</span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={admin || canConfigure}
                disabled={admin}
                onChange={(event) => setCanConfigure(event.target.checked)}
              />
              <span>
                Can change settings
                {admin && <span className="field__note"> — administrators always can</span>}
              </span>
            </label>
            <PropertyPicker available={propertyIds} selected={assigned} disabled={busy} onChange={setAssigned} />
            <div className="admin__actions">
              <button type="button" className="button" disabled={busy || !ready} onClick={add}>
                Create
              </button>
              <button type="button" className="button secondary" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="admin__actions">
            <button type="button" className="button secondary" disabled={busy} onClick={() => setAdding(true)}>
              Add person
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

// --- agents ---

function Agents({
  agents,
  propertyIds,
  busy,
  issuedToken,
  onIssued,
  onChange,
  run,
}: {
  agents: AgentSummary[]
  propertyIds: string[]
  busy: boolean
  issuedToken: { name: string; token: string } | null
  onIssued: (issued: { name: string; token: string } | null) => void
  onChange: (agents: AgentSummary[]) => void
  run: <T>(action: () => Promise<T>, message: string) => Promise<T | null>
}) {
  const [name, setName] = useState('')
  const [assigned, setAssigned] = useState<string[]>([])

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Agents</h2>
        <span className="section__note">Bearer tokens for hermes and anything else on a schedule</span>
      </div>

      {issuedToken && (
        <Banner tone="warning">
          <strong>Token for {issuedToken.name} — copy it now.</strong> Only its digest is stored,
          so this is the only time it can be shown.
          <code className="admin__token">{issuedToken.token}</code>
          <button type="button" className="button secondary" onClick={() => onIssued(null)}>
            Done
          </button>
        </Banner>
      )}

      <div className="card admin__card">
        {agents.map((agent) => (
          <div className="admin__person" key={agent.name}>
            <div className="admin__identity">
              <strong>{agent.name}</strong>
              <span className="admin__meta">{describe(agent.properties)}</span>
            </div>

            <PropertyPicker
              available={propertyIds}
              selected={agent.properties}
              disabled={busy}
              onChange={async (properties) => {
                const saved = await run(
                  () => api.updateAgent(agent.name, properties),
                  `${agent.name} now reads ${describe(properties).toLowerCase()}.`,
                )
                if (saved) onChange(saved)
              }}
            />

            <div className="admin__person-actions">
              <button
                type="button"
                className="button danger"
                disabled={busy}
                onClick={async () => {
                  const saved = await run(
                    () => api.deleteAgent(agent.name),
                    `Revoked ${agent.name}; its token no longer works.`,
                  )
                  if (saved) onChange(saved)
                }}
              >
                Revoke
              </button>
            </div>
          </div>
        ))}

        <div className="admin__row admin__row--new">
          <label className="field">
            <span className="field__label">New agent</span>
            <input value={name} placeholder="hermes" onChange={(event) => setName(event.target.value)} />
          </label>
          <PropertyPicker available={propertyIds} selected={assigned} disabled={busy} onChange={setAssigned} />
          <button
            type="button"
            className="button"
            disabled={busy || name.trim().length === 0}
            onClick={async () => {
              const created = await run(
                () => api.createAgent(name.trim(), assigned),
                `Created ${name.trim()}.`,
              )
              if (created) {
                onIssued({ name: created.name, token: created.token })
                setName('')
                setAssigned([])
                const refreshed = await api.adminAgents()
                onChange(refreshed)
              }
            }}
          >
            Create token
          </button>
        </div>
      </div>
    </section>
  )
}
